/**
 * SliceViewport2D - Abstract base for all 2D slice viewports.
 *
 * Provides:
 * - Orthographic camera projection
 * - vtkImageResliceMapper + vtkImageSlice + vtkPlane rendering pipeline
 * - CurrentLocationService subscription (slice plane repositioning)
 * - ViewportDataService "ct-volume" subscription
 *
 * Subclasses implement:
 * - configureSliceOrientation() — set camera + slice plane for their orientation
 */

import vtkPlane from '@kitware/vtk.js/Common/DataModel/Plane';
import vtkImageResliceMapper from '@kitware/vtk.js/Rendering/Core/ImageResliceMapper';
import vtkImageSlice from '@kitware/vtk.js/Rendering/Core/ImageSlice';

import { BaseViewport } from './base-viewport';
import type { ParallelScaleRange, ViewportOptions, ViewportType, ViewportVtkContext } from './types';
import type { ServiceContext } from '../core/types';
import type { Volume } from '../domain/volume';
import { CurrentLocationService, type CurrentLocation } from '../services/current-location-service';
import { ViewportDataService } from '../services/viewport-data-service';
import { WindowLevelService, type WindowLevel } from '../services/window-level-service';

/** Half-thickness (mm) of the visible slab centred on the slice plane. */
const DEFAULT_CLIPPING_OFFSET = 5;

// Zoom bounds, expressed relative to the reference scale — the parallelScale
// that fits the full slice extent of the volume in the viewport (see
// setZoomReference below). For a typical 350 mm CT extent the reference is
// ~175 mm, which puts the zoom-in floor around a 9 mm visible height.
//
// Both the reference and the last explicit framing are kept as mm extents and
// converted to a parallelScale against the *current* container size, so the
// bounds follow the viewport through resizes without a refresh hook.

/** Deepest zoom-in: parallelScale may not go below reference / this factor. */
const MAX_ZOOM_IN_FACTOR = 40;
/**
 * Widest zoom-out: parallelScale may not exceed reference x this factor.
 * At 1, zooming out stops exactly when the volume fits the viewport — the
 * slice never shrinks into a small image lost in the background.
 * Subclasses tighten it through {@link SliceViewport2D.maxZoomOutFactor}.
 *
 * The bound only limits interactive zoom: an explicit framing wider than it
 * (see {@link SliceViewport2D.fitToViewport}) is always honoured and becomes
 * the zoom-out limit itself.
 */
const MAX_ZOOM_OUT_FACTOR = 1;

/** A rectangle in world millimetres. */
interface Extent {
    width: number;
    height: number;
}

export abstract class SliceViewport2D extends BaseViewport {
    protected volume: Volume | null = null;

    /**
     * Set to true while this viewport is the initiator of a setSliceIndex call.
     * Used by onLocationChanged to skip the redundant self-event fired by the
     * synchronous broadcast inside setSliceIndex.
     */
    protected _isUpdatingSlice = false;

    // Slice-changed listeners — fire after slicePlane + camera are updated.
    // Lazy-initialized because BaseViewport's super() constructor may trigger
    // display object initialization before this field initializer runs.
    private sliceChangedListeners?: Array<() => void>;

    /**
     * Full slice extent (mm) — the anchor the zoom bounds are expressed
     * against. Null until a subclass sets it.
     */
    private zoomReferenceExtent: Extent | null = null;

    /**
     * Extent (mm) of the last explicit framing through fitToViewport. The
     * zoom-out bound never drops below the scale that fits it, so a framing
     * wider than the reference-based cap is honoured rather than clamped.
     */
    private framedExtent: Extent | null = null;

    // VTK reslice pipeline
    protected readonly slicePlane: vtkPlane;
    private resliceMapper: vtkImageResliceMapper | null = null;
    private imageSlice: vtkImageSlice | null = null;

    constructor(
        id: string,
        type: ViewportType,
        vtkContext: ViewportVtkContext,
        context: ServiceContext,
        options?: ViewportOptions
    ) {
        super(id, type, vtkContext, context, options);
        this.slicePlane = vtkPlane.newInstance();
        this.setupOrthographicCamera();
        this.subscribeToVolumeData();
        this.subscribeToLocationService();
        this.subscribeToWindowLevelService();
    }

    // ========================================================================
    // Slice index API
    // ========================================================================

    /**
     * Total number of slices available along the current slice normal.
     * For axis-aligned normals this equals the volume dimension along that axis.
     *
     * NOTE: The L1-style formula is only correct for axis-aligned normals.
     * The oblique subclass overrides this when a centerline is active.
     */
    get sliceCount(): number {
        if (!this.volume) return 0;
        const dims = this.volume.dimensions;
        const n = this.slicePlane.getNormal() as [number, number, number];
        return Math.round(Math.abs(n[0]) * dims[0] + Math.abs(n[1]) * dims[1] + Math.abs(n[2]) * dims[2]);
    }

    /**
     * Current slice index (0-based), derived from the plane origin's
     * position relative to the volume origin projected onto the slice normal.
     */
    get sliceIndex(): number {
        if (!this.volume) return 0;
        const volOrigin = this.volume.origin;
        const planeOrigin = this.slicePlane.getOrigin() as [number, number, number];
        const n = this.slicePlane.getNormal() as [number, number, number];
        const step = this.getSliceStepDistance();
        if (step === 0) return 0;

        const dot =
            (planeOrigin[0] - volOrigin[0]) * n[0] +
            (planeOrigin[1] - volOrigin[1]) * n[1] +
            (planeOrigin[2] - volOrigin[2]) * n[2];
        return Math.round(dot / step);
    }

    get slicePlaneOrigin(): [number, number, number] {
        return this.slicePlane.getOrigin() as [number, number, number];
    }

    get slicePlaneNormal(): [number, number, number] {
        return this.slicePlane.getNormal() as [number, number, number];
    }

    /**
     * The vtkPlane instance used for slicing.
     * Exposed so display objects (e.g. vtkCutter) can share the same reference
     * instead of maintaining a copy that may go out of sync.
     */
    getSlicePlane(): vtkPlane {
        return this.slicePlane;
    }

    /**
     * Subscribe to slice-plane changes.
     * The callback fires after the slice plane origin and camera have been updated,
     * guaranteeing a consistent state for consumers like vtkCutter.
     *
     * @returns An unsubscribe function.
     */
    onSliceChanged(callback: () => void): () => void {
        (this.sliceChangedListeners ??= []).push(callback);
        return () => {
            this.sliceChangedListeners = this.sliceChangedListeners?.filter((cb) => cb !== callback);
        };
    }

    /**
     * Jump to a specific slice index.
     * Updates this viewport immediately (no round-trip through the event bus),
     * then broadcasts the new world position through CurrentLocationService so
     * other viewports stay in sync. The self-event produced by the broadcast is
     * suppressed by the _isUpdatingSlice guard in onLocationChanged.
     *
     * In-plane components are taken from the current service world position
     * so other viewports are not reset when this viewport scrolls — only the
     * component along this viewport's normal is updated.
     */
    setSliceIndex(index: number): void {
        if (!this.volume) return;

        const count = this.sliceCount;
        if (count === 0) return;
        const clamped = Math.max(0, Math.min(index, count - 1));

        const volOrigin = this.volume.origin;
        const n = this.slicePlane.getNormal() as [number, number, number];
        const step = this.getSliceStepDistance();
        if (step === 0) return;

        // Use the current service world position as the base so in-plane
        // components from other viewports are preserved. Fall back to the
        // plane origin only when no location has been published yet.
        const locationService = this.context.services.getService<CurrentLocationService>(
            CurrentLocationService.SERVICE_NAME
        );
        const planeOrigin = this.slicePlane.getOrigin() as [number, number, number];
        const base = locationService.getLocation()?.worldPosition ?? planeOrigin;

        // Replace the normal component with the target slice distance,
        // leaving the two in-plane components of `base` untouched.
        const baseNormalDist = base[0] * n[0] + base[1] * n[1] + base[2] * n[2];
        const targetNormalDist =
            volOrigin[0] * n[0] + volOrigin[1] * n[1] + volOrigin[2] * n[2] + clamped * step;
        const normalDelta = targetNormalDist - baseNormalDist;

        const newWorldPosition: [number, number, number] = [
            base[0] + normalDelta * n[0],
            base[1] + normalDelta * n[1],
            base[2] + normalDelta * n[2],
        ];

        this._isUpdatingSlice = true;
        try {
            this.applyWorldPositionToSlice(newWorldPosition);
            locationService.setCurrentWorldPosition(newWorldPosition);
        } finally {
            this._isUpdatingSlice = false;
        }
    }

    /**
     * Apply a world position directly to the slice plane and camera without
     * going through the service — used by setSliceIndex (local update) and
     * onLocationChanged (update triggered by another viewport).
     */
    protected applyWorldPositionToSlice(worldPosition: [number, number, number]): void {
        if (!this.volume) return;

        const n = this.slicePlane.getNormal() as [number, number, number];
        const step = this.getSliceStepDistance();
        if (step === 0) return;

        const count = this.sliceCount;
        if (count === 0) return;

        const volOrigin = this.volume.origin;
        const planeOrigin = this.slicePlane.getOrigin() as [number, number, number];

        // Project world position onto normal and snap to nearest slice
        const targetDist =
            (worldPosition[0] - volOrigin[0]) * n[0] +
            (worldPosition[1] - volOrigin[1]) * n[1] +
            (worldPosition[2] - volOrigin[2]) * n[2];
        const clamped = Math.max(0, Math.min(Math.round(targetDist / step), count - 1));

        // Compute the new plane origin absolutely from volume origin to avoid
        // floating-point drift that would accumulate with incremental deltas.
        // Only the component along the slice normal is repositioned; the
        // in-plane components stay at the volume origin (axis-aligned normals).
        const sliceDist = clamped * step;
        const newOrigin: [number, number, number] = [
            volOrigin[0] + sliceDist * n[0],
            volOrigin[1] + sliceDist * n[1],
            volOrigin[2] + sliceDist * n[2],
        ];

        // Delta between old and new origin — used to shift the camera by the
        // same amount so the slice stays centred in the viewport.
        const delta =
            (newOrigin[0] - planeOrigin[0]) * n[0] +
            (newOrigin[1] - planeOrigin[1]) * n[1] +
            (newOrigin[2] - planeOrigin[2]) * n[2];

        this.slicePlane.setOrigin(newOrigin[0], newOrigin[1], newOrigin[2]);

        const camera = this.getRenderer(0).getActiveCamera();
        const pos = camera.getPosition() as [number, number, number];
        const fp = camera.getFocalPoint() as [number, number, number];
        camera.setPosition(pos[0] + delta * n[0], pos[1] + delta * n[1], pos[2] + delta * n[2]);
        camera.setFocalPoint(fp[0] + delta * n[0], fp[1] + delta * n[1], fp[2] + delta * n[2]);

        this.updateCameraClippingRange();

        // Notify listeners after plane + camera are fully consistent
        if (this.sliceChangedListeners) {
            for (const cb of this.sliceChangedListeners) cb();
        }

        this.render();
    }

    /**
     * Convert an arbitrary world position to the nearest slice index
     * by projecting onto the slice normal relative to the volume origin.
     */
    protected worldPositionToSliceIndex(worldPosition: [number, number, number]): number {
        if (!this.volume) return 0;
        const volOrigin = this.volume.origin;
        const n = this.slicePlane.getNormal() as [number, number, number];
        const step = this.getSliceStepDistance();
        if (step === 0) return 0;

        const dot =
            (worldPosition[0] - volOrigin[0]) * n[0] +
            (worldPosition[1] - volOrigin[1]) * n[1] +
            (worldPosition[2] - volOrigin[2]) * n[2];
        return Math.round(dot / step);
    }

    /**
     * Voxel spacing projected onto the slice normal — the distance
     * between consecutive slice planes.
     *
     * NOTE: The L1-style formula is only correct for axis-aligned normals.
     * The oblique subclass overrides this when a centerline is active.
     */
    protected getSliceStepDistance(): number {
        if (!this.volume) return 0;
        const s = this.volume.spacing;
        const n = this.slicePlane.getNormal() as [number, number, number];
        return Math.abs(s[0] * n[0]) + Math.abs(s[1] * n[1]) + Math.abs(s[2] * n[2]);
    }

    // ========================================================================
    // Abstract — subclasses configure their specific slice orientation
    // ========================================================================

    /**
     * Configure the camera and slice plane orientation for this viewport.
     * Called when volume data arrives. Subclasses must:
     * - Set camera position/focalPoint/viewUp
     * - Set slicePlane origin and normal
     * - Optionally set parallelScale
     */
    protected abstract configureSliceOrientation(volume: Volume): void;

    /**
     * Set parallelScale so that a rectangle of (sliceWidth x sliceHeight) mm
     * fits exactly inside the current viewport pixel dimensions.
     *
     * Explicit framing is never clamped: the requested extent is applied as is
     * and remembered, and the zoom-out bound widens to include it (see
     * getParallelScaleRange). Only interactive zoom is limited.
     */
    protected fitToViewport(sliceWidth: number, sliceHeight: number): void {
        const scale = this.computeFitParallelScale({ width: sliceWidth, height: sliceHeight });
        if (scale === null) return;

        this.framedExtent = { width: sliceWidth, height: sliceHeight };
        this.getRenderer(0).getActiveCamera().setParallelScale(scale);
    }

    /**
     * The parallelScale at which a (width x height) mm rectangle fits exactly
     * inside the current viewport pixel dimensions, or null when either the
     * container or the rectangle is degenerate.
     */
    private computeFitParallelScale(extent: Extent | null): number | null {
        if (extent == null) return null;
        const { width, height } = extent;
        const { width: vpW, height: vpH } = this.vtkContext.container.getBoundingClientRect();
        if (vpW <= 0 || vpH <= 0 || width <= 0 || height <= 0) return null;

        const viewportAspect = vpW / vpH;
        const sliceAspect = width / height;

        // Wider than the viewport → constrained by width, otherwise by height
        return sliceAspect > viewportAspect ? width / (2 * viewportAspect) : height / 2;
    }

    /**
     * Anchor the zoom bounds to a (width x height) mm extent — the full slice
     * extent of the volume, which is not necessarily the extent the viewport
     * initially frames (the oblique viewport opens on a fixed field of view).
     *
     * Subclasses call this whenever the slice geometry changes. The extent is
     * kept in millimetres and converted to a scale on demand, so the bounds
     * stay correct when the container is resized.
     */
    protected setZoomReference(width: number, height: number): void {
        this.zoomReferenceExtent = width > 0 && height > 0 ? { width, height } : null;
    }

    /**
     * How far out this viewport may zoom, as a multiple of the reference scale.
     *
     * A getter rather than a field so subclasses can override it: a subclass
     * field initializer would only run after the SliceViewport2D constructor
     * has already framed the first slice, and the initial fit would be clamped
     * against the base value.
     */
    protected get maxZoomOutFactor(): number {
        return MAX_ZOOM_OUT_FACTOR;
    }

    /**
     * Zoom bounds relative to the reference scale, evaluated against the
     * current container size. Null until a reference is set — no volume yet,
     * or the container is not laid out — leaving zoom unbounded rather than
     * clamped against a meaningless value.
     *
     * The zoom-out bound is never tighter than the last explicit framing, so
     * a field of view wider than the reference-based cap stays reachable.
     */
    override getParallelScaleRange(): ParallelScaleRange | null {
        // Loose null checks: the field initializers have not necessarily run
        // yet when BaseViewport's constructor reaches display object
        // initialization, so the fields may still be undefined here.
        const reference = this.computeFitParallelScale(this.zoomReferenceExtent ?? null);
        if (reference == null) return null;

        const framed = this.computeFitParallelScale(this.framedExtent ?? null);

        return {
            min: reference / MAX_ZOOM_IN_FACTOR,
            max: Math.max(reference * this.maxZoomOutFactor, framed ?? 0),
        };
    }

    // ========================================================================
    // Camera setup
    // ========================================================================

    private setupOrthographicCamera(): void {
        const camera = this.getRenderer(0).getActiveCamera();
        camera.setParallelProjection(true);
    }

    override updateCameraClippingRange(): void {
        const camera = this.getRenderer(0).getActiveCamera();
        const pos = camera.getPosition() as [number, number, number];
        const fp = camera.getFocalPoint() as [number, number, number];

        const dist = Math.sqrt((fp[0] - pos[0]) ** 2 + (fp[1] - pos[1]) ** 2 + (fp[2] - pos[2]) ** 2);

        camera.setClippingRange(
            Math.max(0.1, dist - DEFAULT_CLIPPING_OFFSET),
            dist + DEFAULT_CLIPPING_OFFSET
        );
    }

    // ========================================================================
    // ViewportDataService subscription
    // ========================================================================

    private subscribeToVolumeData(): void {
        const dataService = this.context.services.getService<ViewportDataService>(
            ViewportDataService.SERVICE_NAME
        );

        const existing = dataService.getSlot('ct-volume');
        if (existing) {
            this.onVolumeDataChanged(existing);
        }

        this.unsubscribers.push(
            dataService.subscribeToSlot('ct-volume', (volume) => {
                this.onVolumeDataChanged(volume);
            })
        );
    }

    private onVolumeDataChanged(volume: Volume): void {
        this.volume = volume;

        this.createReslicePipeline(volume);
        this.configureSliceOrientation(volume);

        this.updateCameraClippingRange();
        this.render();
    }

    // ========================================================================
    // Reslice rendering pipeline
    // ========================================================================

    private createReslicePipeline(volume: Volume): void {
        // Dispose previous pipeline if replacing volume
        this.disposeReslicePipeline();

        // Create mapper with the slice plane
        this.resliceMapper = vtkImageResliceMapper.newInstance();
        this.resliceMapper.setInputData(volume.imageData);
        this.resliceMapper.setSlicePlane(this.slicePlane);

        // Create the image slice actor
        this.imageSlice = vtkImageSlice.newInstance();
        this.imageSlice.setMapper(this.resliceMapper);

        // Apply current W/L from service
        const wlService = this.context.services.getService<WindowLevelService>(
            WindowLevelService.SERVICE_NAME
        );
        this.applyWindowLevel(wlService.getWindowLevel());

        // Ensure proper interpolation mode (nearest neighbor by default)
        const property = this.imageSlice.getProperty();
        property.setInterpolationTypeToLinear();

        // Add to renderer
        this.getRenderer(0).addActor(this.imageSlice);
    }

    private disposeReslicePipeline(): void {
        if (this.imageSlice) {
            this.getRenderer(0).removeActor(this.imageSlice);
        }
        this.imageSlice?.delete();
        this.resliceMapper?.delete();
        this.imageSlice = null;
        this.resliceMapper = null;
    }

    // ========================================================================
    // CurrentLocationService subscription
    // ========================================================================

    private subscribeToLocationService(): void {
        const locationService = this.context.services.getService<CurrentLocationService>(
            CurrentLocationService.SERVICE_NAME
        );

        const existing = locationService.getLocation();
        if (existing) {
            this.onLocationChanged(existing);
        }

        this.unsubscribers.push(
            locationService.subscribe<CurrentLocation>(locationService.EVENTS.LOCATION_CHANGED, (location) =>
                this.onLocationChanged(location)
            )
        );
    }

    /**
     * Move slice plane and camera to the nearest discrete slice at the given world position.
     * No-op when this viewport is the initiator of the current setSliceIndex call.
     */
    protected onLocationChanged(location: CurrentLocation): void {
        if (this._isUpdatingSlice) return;
        this.applyWorldPositionToSlice(location.worldPosition);
    }

    // ========================================================================
    // WindowLevelService subscription
    // ========================================================================

    private subscribeToWindowLevelService(): void {
        const wlService = this.context.services.getService<WindowLevelService>(
            WindowLevelService.SERVICE_NAME
        );

        // Apply current values (may differ from defaults if service was updated before viewport created)
        this.applyWindowLevel(wlService.getWindowLevel());

        this.unsubscribers.push(
            wlService.subscribe<WindowLevel>(wlService.EVENTS.WINDOW_LEVEL_CHANGED, (wl) => {
                this.applyWindowLevel(wl);
                this.render();
            })
        );
    }

    private applyWindowLevel(wl: WindowLevel): void {
        if (!this.imageSlice) return;
        const property = this.imageSlice.getProperty();
        property.setColorWindow(wl.window);
        property.setColorLevel(wl.level);
    }

    // ========================================================================
    // Lifecycle
    // ========================================================================

    override dispose(): void {
        this.sliceChangedListeners = undefined;
        this.disposeReslicePipeline();
        this.slicePlane.delete();
        super.dispose();
    }
}
